import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useAiScheduleAccess } from '@frontend/ai-receptionist-settings';
import { useTranslation } from '@frontend/i18n';
import { useMatchMedia } from '@frontend/responsiveness';
import { useCalendarHeaderContext } from '../../../../context/calendar-header-context';
import { useShouldShowCalendarInsuranceCount } from '../../../../hooks/use-should-show-calendar-insurance-count';
import { useCalendarStore } from '../../../../stores/calendar.store';
import { openSchedulePulseWithFilters, SchedulePulseTab } from '../../../../stores/schedule-pulse.store';
import { CALENDAR_TRACKING_IDS } from '../../../../trackingIds/calendar-tracking-ids';
import { QuickFilters } from './quick-filters';
import { QuickFilterKey, QuickFilterListItem } from './quick-filters/types';

export const QuickFiltersContainer = () => {
  const { t } = useTranslation('schedule');
  const isMedium = useMatchMedia({ maxWidth: 1160 });
  const { calendarEventsCount, unconfirmedCalendarEventsCount, insuranceAlertCalendarEventsCount } =
    useCalendarHeaderContext();
  const { calendarDate } = useCalendarStore(['calendarDate']);
  const { isScheduleV3FlagEnabled } = SchedulerV3.Helpers.useGetSchedulerV3FeatureFlagDetails();
  const shouldShowCalendarInsuranceCount = useShouldShowCalendarInsuranceCount();
  const { hasAiReceptionistAccess } = useAiScheduleAccess();

  const handleSelection = (filter: QuickFilterKey) => {
    openSchedulePulseWithFilters({
      selectedTab: filter === QuickFilterKey.Insurance ? SchedulePulseTab.Insurance : SchedulePulseTab.AppointmentList,
      appointmentListTabState: {
        calendarDate: calendarDate || '',
        isUnconfirmedStatusSelected: filter === QuickFilterKey.Unconfirmed,
      },
    });
  };
  const quickFilterList = useMemo<QuickFilterListItem[]>(() => {
    const list: QuickFilterListItem[] = [
      {
        key: QuickFilterKey.Unconfirmed,
        label: t('Unconfirmed'),
        iconName: 'pending',
        count: unconfirmedCalendarEventsCount ?? 0,
        trackingId: CALENDAR_TRACKING_IDS.headerBar.unconfirmedAppointmentsCount,
      },
    ];
    if (!hasAiReceptionistAccess) {
      list.unshift({
        key: QuickFilterKey.All,
        label: t('Appointments'),
        iconName: 'calendar',
        count: calendarEventsCount ?? 0,
        trackingId: CALENDAR_TRACKING_IDS.headerBar.allAppointmentsCountButton,
      });
    }

    // TODO: update this once insurance support added in v3
    if (shouldShowCalendarInsuranceCount) {
      list.push({
        key: QuickFilterKey.Insurance,
        label: t('Insurance'),
        iconName: 'shield',
        count: insuranceAlertCalendarEventsCount ?? 0,
        trackingId: CALENDAR_TRACKING_IDS.headerBar.insuranceAlertsCount,
      });
    }

    return list;
  }, [
    calendarEventsCount,
    t,
    unconfirmedCalendarEventsCount,
    insuranceAlertCalendarEventsCount,
    shouldShowCalendarInsuranceCount,
    isScheduleV3FlagEnabled,
    hasAiReceptionistAccess,
  ]);

  return <QuickFilters variant={isMedium ? 'dropdown' : 'card'} list={quickFilterList} onSelect={handleSelection} />;
};
